import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star } from 'lucide-react';
import { Section } from './Shared';
type PlanId = 'esencial' | 'avanzado' | 'oro';
const plans: {
  id: PlanId;
  emoji: string;
  name: string;
  desc: string;
  ideal: string;
  oldPrice: string;
  price: string;
  href: string;
  recommended: boolean;
  includes: string[];
}[] = [{
  id: 'esencial',
  emoji: '📘',
  name: 'KIT ESENCIAL',
  desc: 'El sistema de limpieza y protección energética para cortar la mala racha y blindar tu hogar.',
  ideal: 'Ideal para quien necesita limpiar su casa, frenar la mala racha y protegerse contra la envidia de forma urgente.',
  oldPrice: '$52.000',
  price: '$19.990 ARS',
  href: 'https://www.lumenakasha.com/cart/47029498773693:1',
  recommended: false,
  includes: ['✅ Código de Blindaje Argentino™ (6 Volúmenes — 100 Pág.)', '🎁 La Bóveda de los Sigilos Digitales', '🎁 Guía Rápida: Sahumado de Almacén', '🎁 El Escudo de San Jorge para el Hogar']
}, {
  id: 'avanzado',
  emoji: '⭐',
  name: 'KIT AVANZADO',
  desc: 'El sistema completo para limpiar, proteger y reprogramar tu relación con el dinero todos los días.',
  ideal: 'Ideal para quien quiere proteger su casa y además cambiar el chip de escasez cada vez que gasta o cobra.',
  oldPrice: '$71.000',
  price: '$27.990 ARS',
  href: 'https://www.lumenakasha.com/cart/47029498839229:1',
  recommended: true,
  includes: ['✅ TODO lo del Kit Esencial.', '➕ El Oráculo de la Billetera: 7 Decretos de Activación Diaria.']
}, {
  id: 'oro',
  emoji: '🌟',
  name: 'COMBO DE ORO',
  desc: 'El arsenal completo de limpieza, protección, activación diaria y expansión de ingresos.',
  ideal: 'Ideal para quien no quiere dejar nada al azar y quiere pasar de sobrevivir a expandir su economía de forma masiva.',
  oldPrice: '$95.000',
  price: '$34.990 ARS',
  href: 'https://www.lumenakasha.com/cart/47029499363517:1',
  recommended: false,
  includes: ['✅ TODO lo del Kit Esencial.', '✅ TODO lo del Kit Avanzado.', '➕ El Protocolo de Apertura Maestra: Sinfonía de Prosperidad (6 Volúmenes Extra — 50 Pág.)']
}];
export const Pricing = () => {
  const [selected, setSelected] = useState<PlanId>('avanzado');
  const selectedPlan = plans.find(p => p.id === selected)!;
  return <Section id="pricing" className="bg-brand-surfaceLight/60 rounded-3xl my-12 border border-brand-gold/20" data-id="element-355">
      <div className="text-center mb-10" data-id="element-356">
        <h2 className="text-2xl md:text-4xl font-display font-bold text-brand-darkGreen" data-id="element-357">
          ELEGÍ TU <span className="green-gradient-text" data-id="element-358">KIT</span>
        </h2>
        <p className="text-brand-text/70 mt-3 text-sm md:text-base" data-id="element-359">
          ⏰ El precio de lanzamiento finaliza pronto. Después aumenta y no
          vuelve.
        </p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4" data-id="element-360">
        {plans.map(plan => {
        const isSelected = selected === plan.id;
        return <motion.button key={plan.id} type="button" onClick={() => setSelected(plan.id)} whileTap={{
          scale: 0.99
        }} aria-pressed={isSelected} className={`w-full text-left rounded-2xl md:rounded-3xl p-4 md:p-6 border-2 transition-all duration-300 ${isSelected ? 'border-brand-goldDark bg-white shadow-[0_0_24px_rgba(212,175,55,0.2)]' : 'border-brand-gold/20 bg-white/70 hover:border-brand-gold/50'}`} data-id="element-361">
              <div className="flex items-start gap-3 md:gap-4" data-id="element-362">
                <div className="shrink-0 mt-1" data-id="element-363">
                  <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-brand-goldDark' : 'border-brand-gold/40'}`} data-id="element-364">
                    {isSelected && <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-brand-goldDark" data-id="element-365" />}
                  </div>
                </div>

                <div className="flex-1 min-w-0" data-id="element-366">
                  {plan.recommended && <div className="flex items-center gap-1.5 mb-1.5 text-brand-goldDark" data-id="element-367">
                      <Trophy className="w-4 h-4 fill-brand-goldDark" data-id="element-368" />
                      <Star className="w-3.5 h-3.5 fill-brand-goldDark" data-id="element-369" />
                      <span className="text-[10px] md:text-sm font-bold tracking-wider uppercase" data-id="element-370">
                        Más elegido
                      </span>
                    </div>}

                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1" data-id="element-371">
                    <div className="min-w-0" data-id="element-372">
                      <h3 className="font-serif font-bold text-sm md:text-lg text-brand-darkGreen leading-tight" data-id="element-373">
                        <span className="mr-1.5" data-id="element-374">{plan.emoji}</span>
                        {plan.name}
                      </h3>
                      <p className="text-xs md:text-sm text-brand-text/70 mt-1" data-id="element-375">
                        {plan.desc}
                      </p>
                      <p className="text-[11px] md:text-sm text-brand-goldDark font-medium mt-1.5" data-id="element-376">
                        {plan.ideal}
                      </p>
                    </div>

                    <div className="text-right shrink-0" data-id="element-377">
                      <p className="text-xs md:text-sm text-brand-text/40 line-through leading-none" data-id="element-378">
                        {plan.oldPrice}
                      </p>
                      <p className={`font-serif font-bold text-lg md:text-2xl leading-tight mt-1 ${isSelected ? 'text-brand-goldDark' : 'text-brand-text'}`} data-id="element-379">
                        {plan.price}
                      </p>
                    </div>
                  </div>

                  {isSelected && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} className="mt-4 pt-4 border-t border-brand-gold/10" data-id="element-380">
                      <ul className="space-y-1.5 text-sm" data-id="element-381">
                        {plan.includes.map((item, i) => <li key={i} className="text-brand-text/80" data-id="element-382">
                            {item}
                          </li>)}
                      </ul>
                    </motion.div>}
                </div>
              </div>
            </motion.button>;
      })}

        <motion.a href={selectedPlan.href} animate={{
        scale: [1, 1.025, 1],
        y: [0, -2, 0]
      }} transition={{
        duration: 2.4,
        ease: 'easeInOut',
        repeat: Infinity
      }} whileHover={{
        scale: 1.04,
        y: -3,
        transition: {
          duration: 0.25
        }
      }} whileTap={{
        scale: 0.98
      }} className="mt-6 w-full inline-flex items-center justify-center px-6 py-4 md:py-5 text-sm md:text-lg font-bold text-white green-gradient-bg rounded-full animate-glow-pulse relative overflow-hidden cta-shimmer shadow-[0_0_20px_rgba(101,163,13,0.4)]" data-id="element-383">
          <span className="relative z-10" data-id="element-384">
            → LO QUIERO {selectedPlan.price.split(' ')[0]}
          </span>
        </motion.a>

        <p className="text-center text-xs md:text-sm text-brand-text/60 pt-2" data-id="element-385">
          🛡️ Garantía 7 días · 📥 Descarga inmediata · 🔒 Pago seguro · 💳
          Mercado Pago
        </p>
      </div>
    </Section>;
};